import { useState, useCallback, useRef, useEffect } from 'react';
import { useStepProgress } from '../../../hooks/useStepProgress';
import { useStepChecklist } from '../../../hooks/useStepChecklist';
import { supabase } from '../../../lib/supabase';
import FullCompletionCelebration from '../../../components/base/FullCompletionCelebration';

interface ResearchGuideProps {
  user: any;
  scrollToSection: (sectionId: string) => void;
}

const researchSteps = [
  {
    id: 1,
    title: 'テーマ設定',
    subtitle: '研究の出発点を定める',
    icon: 'ri-lightbulb-line',
    color: 'from-amber-500 to-orange-500',
    bgLight: 'from-amber-50 to-orange-50',
    importance:
      '研究の方向性を決める最も重要な段階です。テーマが明確でないと、後の全ての作業が曖昧になります。',
    concreteActions: [
      '新聞・ニュース・授業で気になったトピックを毎日1つメモする',
      'Google Scholarで関連キーワードを検索し、論文が何本ヒットするか確認する',
      '「〇〇 × △△」のようにキーワードを掛け合わせて、ニッチな領域を探す',
      '指導教員のオフィスアワーに3つのテーマ候補を持参して相談する',
      '選んだテーマについて「誰の・何の・どんな問題を解決するのか」を1文で書く',
    ],
    goals: [
      '自分の興味と学術的価値が交差する領域を見つける',
      '先行研究の空白や課題を特定する',
      '研究可能な範囲に絞り込む',
      '仮のリサーチクエスチョンを立てる',
    ],
    tips: [
      '興味のあるキーワードを5〜10個書き出し、組み合わせを試す',
      '指導教員に早めに相談し、方向性の確認を取る',
      '仮タイトルを3案作り、それぞれの実現可能性を比較する',
      '「なぜ今この研究が必要なのか」を3文で説明できるようにする',
    ],
    portfolioLink: { text: 'テーマと目的を編集', section: 'overview' },
  },
  {
    id: 2,
    title: '先行研究レビュー',
    subtitle: '学術的基盤を構築する',
    icon: 'ri-book-open-line',
    color: 'from-sky-500 to-cyan-500',
    bgLight: 'from-sky-50 to-cyan-50',
    importance:
      '先行研究レビューは、あなたの研究が「学術的な会話」の一部であることを示します。分野の主要な議論を理解し、自分の研究をその文脈に位置づけることが重要です。',
    concreteActions: [
      'CiNiiやGoogle Scholarで主要キーワードを検索し、被引用数の多い論文トップ10をリストアップする',
      '各論文のAbstractを読み、「目的・方法・結果・限界」を表形式でまとめる',
      '論文の参考文献リストから芋づる式に重要論文を見つける（スノーボール法）',
      '先行研究を「賛成派」「反対派」「条件付き」に分類し、議論の構図を把握する',
      'レビューマトリックス（Excel等）を作成し、論文間の比較を一覧化する',
    ],
    goals: [
      '分野の主要な理論・概念・用語を理解する',
      '既存研究の成果と限界を整理する',
      '自分の研究の位置づけを明確にする',
      '研究の「空白」や「次の一歩」を特定する',
    ],
    tips: [
      '重要論文は最低20〜30本は読む（分野による）',
      '各論文を「目的・方法・結果・限界」の4点でメモする',
      '先行研究を「理論的研究」「実証研究」「方法論研究」などに分類する',
      'レビューマトリックス（表形式）で先行研究を比較整理する',
    ],
    portfolioLink: { text: '参考文献を追加', section: 'notes' },
  },
  {
    id: 3,
    title: 'リサーチクエスチョン策定',
    subtitle: '研究の核心を問いにする',
    icon: 'ri-question-line',
    color: 'from-rose-500 to-pink-500',
    bgLight: 'from-rose-50 to-pink-50',
    importance:
      'リサーチクエスチョン（RQ）は研究の心臓部です。明確なRQがあれば、研究設計・データ収集・分析の全てが一貫します。',
    concreteActions: [
      '先行研究レビューで見つけた「空白」を箇条書きにし、それぞれを疑問文に変換する',
      '各疑問文に対して「データで検証できるか？」「半年〜1年で答えられるか？」をチェックする',
      'メインRQを「〇〇は△△にどのような影響を与えるか」の形式で1文にまとめる',
      'サブRQを2〜3つ設定し、メインRQを分解する形で構成する',
      'RQから逆算して「必要なデータ」「分析手法」「期待される結果」を書き出す',
    ],
    goals: [
      '先行研究の空白から具体的な問いを立てる',
      '答えられる（検証可能な）問いに絞り込む',
      'メインRQとサブRQを階層的に整理する',
      'RQに答えるための方法論の見通しを立てる',
    ],
    tips: [
      'RQは「〜は〜に影響を与えるか」「〜の要因は何か」など具体的に',
      'メインRQ1つ、サブRQ2〜3つの構成が標準的',
      'RQを指導教員に見せて「これで答えられるか」を確認する',
      'RQから逆算して「どんなデータが必要か」をリストアップする',
    ],
    portfolioLink: { text: 'テーマと目的を編集', section: 'overview' },
  },
  {
    id: 4,
    title: '研究設計',
    subtitle: '答えを導く戦略を立てる',
    icon: 'ri-compass-3-line',
    color: 'from-teal-500 to-emerald-500',
    bgLight: 'from-teal-50 to-emerald-50',
    importance:
      '研究設計は「どうやってRQに答えるか」の設計図です。データの種類、収集方法、分析手法を明確にします。',
    concreteActions: [
      '先行研究で使われている分析手法を3つ以上リストアップし、長所・短所を比較する',
      '使用するデータの入手先（政府統計、アンケート、企業データ等）に問い合わせて利用条件を確認する',
      '変数リスト（被説明変数・説明変数・制御変数）を表形式で整理する',
      'サンプルサイズの目安を先行研究から確認し、自分のデータで十分か検討する',
      '分析の手順をフローチャートで図示し、各ステップの所要時間を見積もる',
    ],
    goals: [
      '研究アプローチ（量的・質的・混合）を決定する',
      '必要なデータの種類と入手方法を特定する',
      '分析手法（統計手法・質的分析手法）を選定する',
      'サンプルサイズ・対象期間・対象地域を決める',
    ],
    tips: [
      '先行研究で使われている方法を参考にする',
      'データ入手先に事前に問い合わせて実現可能性を確認する',
      '分析手法の前提条件（正規性検定・等分散性検定等）を確認する',
      '研究倫理審査が必要な場合は早めに申請する',
    ],
    portfolioLink: { text: '分析方法を作成', section: 'designs' },
  },
  {
    id: 5,
    title: 'データ収集',
    subtitle: '証拠を集める',
    icon: 'ri-database-2-line',
    color: 'from-indigo-500 to-sky-500',
    bgLight: 'from-indigo-50 to-sky-50',
    importance:
      'データ収集は研究の「証拠集め」です。質の高いデータがなければ、どんなに優れた分析手法も意味がありません。',
    concreteActions: [
      'データ収集計画書を作成する（いつ・どこから・どうやって・どのくらい集めるか）',
      '生データをダウンロードしたら、日付入りのフォルダに保存し、Googleドライブ等にもバックアップする',
      'Excelやスプレッドシートでデータ収集ログ（取得日・出典・件数・備考）を記録する',
      'データの欠損値・外れ値を確認し、処理方針（除外・補完・そのまま）を決めて記録する',
      '基本統計量（平均・中央値・標準偏差・最大最小）を算出し、データの全体像を把握する',
    ],
    goals: [
      '研究設計に基づいてデータを収集する',
      'データの質を確認し、欠損や異常値に対処する',
      'データ収集の過程を記録する',
      'データを分析可能な形式に整理する',
    ],
    tips: [
      'データ収集ログ（いつ・どこから・どうやって取得したか）を作る',
      '生データは必ずバックアップを取る（複数箇所に保存）',
      'データクリーニングの手順を記録し、再現可能にする',
      'データの基本統計量（平均・標準偏差・分布）を確認する',
    ],
    portfolioLink: null,
  },
  {
    id: 6,
    title: 'データ分析',
    subtitle: '答えを導き出す',
    icon: 'ri-line-chart-line',
    color: 'from-red-500 to-rose-500',
    bgLight: 'from-red-50 to-rose-50',
    importance:
      'データ分析は研究の「答え合わせ」です。RQに対する答えをデータから導き出します。',
    concreteActions: [
      'まず記述統計（度数分布表・ヒストグラム・相関行列）を作成し、データの特徴を視覚化する',
      '分析手法の前提条件（正規性検定・等分散性検定等）を実行し、結果を記録する',
      '本分析（回帰分析・t検定・カイ二乗検定等）を実行し、係数・p値・信頼区間を表にまとめる',
      '頑健性チェック（変数の追加・除外、サンプルの変更等）を行い、結果の安定性を確認する',
      '分析コード（R/Python/Stata等）にコメントを付けて保存し、第三者が再現できるようにする',
    ],
    goals: [
      '研究設計で決めた分析手法を実行する',
      '分析結果を図表にまとめる',
      '結果が統計的に有意か、実質的に意味があるかを判断する',
      '結果からRQへの答えを導く',
    ],
    tips: [
      '分析スクリプト（RやPythonのコード）を保存し、再現可能にする',
      '結果は「記述統計→推測統計→追加分析」の順で整理する',
      '図表は論文に載せる前提で丁寧に作成する',
      '予想外の結果が出たら、データや手法を再確認する',
    ],
    portfolioLink: { text: '図表を追加', section: 'figures' },
  },
  {
    id: 7,
    title: '考察・執筆',
    subtitle: '研究を物語にする',
    icon: 'ri-edit-line',
    color: 'from-fuchsia-500 to-pink-500',
    bgLight: 'from-fuchsia-50 to-pink-50',
    importance:
      '考察・執筆は研究を「学術的な物語」として完成させる段階です。論理的な文章構成と、結果から導かれる洞察の深さが重要です。',
    concreteActions: [
      '分析結果を1段落で要約し、RQに対する答えを明確に述べる',
      '結果を先行研究と比較し、「一致する点」「異なる点」「新たに分かった点」を整理する',
      '研究の限界を3つ以上挙げ、それぞれについて「なぜ限界なのか」「どうすれば克服できるか」を書く',
      '論文の構成（序論→先行研究→方法→結果→考察→結論）に沿って各セクションの草稿を書く',
      '指導教員に草稿を提出し、フィードバックに基づいて修正する（最低2回は繰り返す）',
    ],
    goals: [
      '結果が先行研究とどう関連するかを考察する',
      '研究の学術的・社会的意義を明確にする',
      '研究の限界と今後の課題を正直に述べる',
      '論文を学術的な文体で執筆する',
    ],
    tips: [
      '考察は「結果の要約→先行研究との比較→理論的含意→実務的含意→限界→今後の課題」の順で書く',
      '各セクションの最初に「このセクションで何を述べるか」を明示する',
      '指導教員に草稿を見せて、論理の飛躍がないか確認する',
      '論文の型（IMRAD: Introduction, Methods, Results, And Discussion）を守る',
    ],
    portfolioLink: { text: '資料を追加', section: 'outputs' },
  },
  {
    id: 8,
    title: '発表準備',
    subtitle: '研究を伝える',
    icon: 'ri-presentation-line',
    color: 'from-emerald-500 to-teal-500',
    bgLight: 'from-emerald-50 to-teal-50',
    importance:
      '発表準備は研究を「他者に伝える」技術です。限られた時間で研究の核心を明確に伝えることが重要です。',
    concreteActions: [
      'スライドの構成案を紙に書き出す（タイトル→背景→RQ→方法→結果→考察→結論の順）',
      '各スライドに「このスライドで伝えたい1つのメッセージ」を書き、それ以外の情報を削る',
      '発表原稿を書き、声に出して読み上げて時間を計測する（制限時間の90%に収める）',
      '想定質問を10個リストアップし、それぞれ30秒以内で答える練習をする',
      '友人や家族の前でリハーサルを行い、「分かりにくかった点」をフィードバックしてもらう',
    ],
    goals: [
      '発表スライドを作成する（10〜20枚程度）',
      '発表原稿を準備する',
      '想定質問とその回答を準備する',
      'リハーサルを行い、フィードバックを得る',
    ],
    tips: [
      'スライドは「1枚1メッセージ」の原則で作る',
      '発表時間の70%を本編、30%を質疑応答に配分する',
      '最低3回はリハーサルを行う',
      '「なぜこの研究をしたのか」「何が新しいのか」「何が分かったのか」の3点を明確に',
    ],
    portfolioLink: { text: '資料を追加', section: 'outputs' },
  },
];

const stepChecklists: Record<number, Array<{ id: number; text: string }>> = {
  1: [
    { id: 1, text: '興味のあるキーワードを5〜10個書き出す' },
    { id: 2, text: 'キーワードの組み合わせを試して、テーマ候補を3つ作る' },
    { id: 3, text: '各テーマ候補について、先行研究を3〜5本ずつ検索する' },
    { id: 4, text: '指導教員にテーマ候補を相談し、フィードバックをもらう' },
    { id: 5, text: '仮のリサーチクエスチョンを1〜2文で書く' },
    { id: 6, text: '「なぜ今この研究が必要なのか」を3文で説明できるようにする' },
    { id: 7, text: 'データ入手の難易度を調べる' },
  ],
  2: [
    { id: 1, text: '分野の主要な論文を20〜30本リストアップする' },
    { id: 2, text: '各論文を「目的・方法・結果・限界」の4点でメモする' },
    { id: 3, text: '先行研究を「理論的研究」「実証研究」などに分類する' },
    { id: 4, text: 'レビューマトリックス（表形式）で先行研究を比較整理する' },
    { id: 5, text: '先行研究の「空白」や「限界」を3つ以上特定する' },
    { id: 6, text: '自分の研究が先行研究とどう違うかを明確にする' },
    { id: 7, text: '先行研究レビューの草稿を書く（1000〜2000字程度）' },
  ],
  3: [
    { id: 1, text: '先行研究の空白から、具体的な問いを書き出す' },
    { id: 2, text: '各問いが「検証可能か」「データで答えられるか」を確認する' },
    { id: 3, text: 'メインRQを1つに絞り込む' },
    { id: 4, text: 'サブRQを2〜3つ設定する' },
    { id: 5, text: 'RQに答えるために必要なデータをリストアップする' },
    { id: 6, text: '指導教員にRQを見せて、フィードバックをもらう' },
    { id: 7, text: 'RQを修正し、最終版を確定する' },
  ],
  4: [
    { id: 1, text: '研究アプローチ（量的・質的・混合）を決定する' },
    { id: 2, text: '必要なデータの種類と入手方法を特定する' },
    { id: 3, text: 'データ入手先に問い合わせて、実現可能性を確認する' },
    { id: 4, text: '分析手法（統計手法・質的分析手法）を選定する' },
    { id: 5, text: 'サンプルサイズ・対象期間・対象地域を決める' },
    { id: 6, text: '研究倫理審査が必要か確認し、必要なら申請する' },
    { id: 7, text: '研究設計の草稿を書く（500〜1000字程度）' },
  ],
  5: [
    { id: 1, text: 'データ収集の具体的な手順を書き出す' },
    { id: 2, text: 'データ収集を開始する' },
    { id: 3, text: 'データ収集ログ（いつ・どこから・どうやって）を記録する' },
    { id: 4, text: '生データのバックアップを複数箇所に保存する' },
    { id: 5, text: 'データの欠損や異常値を確認する' },
    { id: 6, text: 'データクリーニングの手順を記録する' },
    { id: 7, text: 'データの基本統計量（平均・標準偏差・分布）を確認する' },
  ],
  6: [
    { id: 1, text: '分析手法の前提条件（正規性・等分散性等）を確認する' },
    { id: 2, text: '記述統計（平均・標準偏差・相関等）を算出する' },
    { id: 3, text: '推測統計（回帰分析・検定等）を実行する' },
    { id: 4, text: '分析結果を図表にまとめる' },
    { id: 5, text: '結果が統計的に有意か、実質的に意味があるかを判断する' },
    { id: 6, text: '分析スクリプト（コード）を保存し、再現可能にする' },
    { id: 7, text: '予想外の結果があれば、データや手法を再確認する' },
  ],
  7: [
    { id: 1, text: '結果の要約を書く（500字程度）' },
    { id: 2, text: '結果を先行研究と比較・対照する' },
    { id: 3, text: '研究の理論的含意を書く' },
    { id: 4, text: '研究の実務的含意を書く' },
    { id: 5, text: '研究の限界を正直に書く' },
    { id: 6, text: '今後の研究課題を書く' },
    { id: 7, text: '論文全体を通読し、論理の飛躍がないか確認する' },
  ],
  8: [
    { id: 1, text: '発表スライドを作成する（10〜20枚程度）' },
    { id: 2, text: '発表原稿を準備する' },
    { id: 3, text: '発表時間を計測し、時間内に収まるよう調整する' },
    { id: 4, text: '想定質問を10個リストアップする' },
    { id: 5, text: '各質問への回答を準備する' },
    { id: 6, text: 'リハーサルを3回以上行う' },
    { id: 7, text: '友人や指導教員にリハーサルを見てもらい、フィードバックをもらう' },
  ],
};

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -30px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

// Hook to detect when a step card enters the viewport for auto-highlight
function useScrollHighlight() {
  const refs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [highlightedStepId, setHighlightedStepId] = useState<number | null>(null);
  const hasTriggered = useRef<Set<number>>(new Set());

  const setRef = useCallback((stepId: number, node: HTMLDivElement | null) => {
    if (node) {
      refs.current.set(stepId, node);
    } else {
      refs.current.delete(stepId);
    }
  }, []);

  const triggerHighlight = useCallback((stepId: number) => {
    if (hasTriggered.current.has(stepId)) return;
    hasTriggered.current.add(stepId);
    setHighlightedStepId(stepId);
    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setHighlightedStepId((prev) => (prev === stepId ? null : prev));
    }, 3000);
  }, []);

  return { setRef, highlightedStepId, triggerHighlight };
}

function ExpandedContent({
  step,
  user,
  completionRate,
  stepChecklists: checklists,
  getItemProgress,
  togglingItem,
  handleChecklistToggle,
  getStepProgress,
  memoTexts,
  setMemoTexts,
  memoEditing,
  setMemoEditing,
  memoSaving,
  memoSaved,
  handleMemoSave,
  getNextAction,
  scrollToSection,
}: any) {
  const childDelays = [0, 0.06, 0.12, 0.18, 0.24, 0.3];

  return (
    <div className="px-5 pb-6 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-5 overflow-hidden">
      {/* Concrete Actions */}
      <div className="animate-expand-child" style={{ animationDelay: `${childDelays[0]}s` }}>
        <div className={`bg-gradient-to-br ${step.bgLight} dark:bg-gray-700/50 rounded-xl p-5 border border-gray-200/60 dark:border-gray-600`}>
          <div className="flex items-center gap-2.5 mb-3">
            <div className={`w-8 h-8 bg-gradient-to-br ${step.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
              <i className="ri-hammer-line text-sm"></i>
            </div>
            <h4 className="font-bold text-gray-900 dark:text-white text-sm">具体的にやること</h4>
          </div>
          <ol className="space-y-2.5">
            {step.concreteActions.map((action: string, idx: number) => (
              <li key={idx} className="flex items-start gap-3">
                <div className={`w-6 h-6 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5 shadow-sm`}>
                  {idx + 1}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed flex-1">{action}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Checklist */}
      {user && checklists[step.id] && (
        <div className="animate-expand-child" style={{ animationDelay: `${childDelays[1]}s` }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-gray-600 dark:text-gray-300 flex-shrink-0">
                <i className="ri-task-line text-sm"></i>
              </div>
              <h4 className="font-bold text-gray-900 dark:text-white text-sm flex-1">ToDoチェックリスト</h4>
              <div className="flex items-center gap-2">
                <div className="w-20 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${step.color} rounded-full transition-all duration-500`}
                    style={{ width: `${completionRate}%` }}
                  ></div>
                </div>
                <span className="text-xs font-bold text-gray-600 dark:text-gray-300">{completionRate}%</span>
              </div>
            </div>
            <ul className="space-y-1">
              {checklists[step.id].map((item: any) => {
                const itemProgress = getItemProgress(step.id, item.id);
                const isCompleted = itemProgress?.is_completed || false;
                const isToggling = togglingItem === `${step.id}-${item.id}`;
                return (
                  <li
                    key={item.id}
                    className={`flex items-start gap-2.5 p-2.5 rounded-lg transition-all duration-300 ${
                      isCompleted ? 'bg-emerald-50/50 dark:bg-emerald-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <button
                      onClick={() => handleChecklistToggle(step.id, item.id)}
                      disabled={isToggling}
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300 cursor-pointer ${
                        isCompleted ? 'bg-emerald-500 border-emerald-500 scale-110' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 hover:border-teal-400'
                      } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isCompleted && <i className="ri-check-line text-white text-xs"></i>}
                    </button>
                    <span className={`text-sm text-gray-700 dark:text-gray-300 leading-relaxed flex-1 transition-all duration-300 ${isCompleted ? 'line-through opacity-50' : ''}`}>
                      {item.text}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Info Grid */}
      <div className="animate-expand-child" style={{ animationDelay: `${childDelays[2]}s` }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 border border-gray-200/60 dark:border-gray-600">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center text-gray-600 dark:text-gray-300 flex-shrink-0">
                <i className="ri-information-line text-sm"></i>
              </div>
              <h4 className="font-bold text-gray-900 dark:text-white text-sm">なぜ重要か</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{step.importance}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 border border-gray-200/60 dark:border-gray-600">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center text-gray-600 dark:text-gray-300 flex-shrink-0">
                <i className="ri-target-line text-sm"></i>
              </div>
              <h4 className="font-bold text-gray-900 dark:text-white text-sm">達成すべきこと</h4>
            </div>
            <ul className="space-y-2">
              {step.goals.map((goal: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5 flex-shrink-0 text-xs"></i>
                  <span>{goal}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="animate-expand-child" style={{ animationDelay: `${childDelays[3]}s` }}>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 border border-amber-200/60 dark:border-amber-700/40">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center text-white flex-shrink-0">
              <i className="ri-lightbulb-flash-line text-sm"></i>
            </div>
            <h4 className="font-bold text-gray-900 dark:text-white text-sm">実践のヒント</h4>
          </div>
          <ul className="space-y-2">
            {step.tips.map((tip: string, idx: number) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                <span className="text-amber-500 flex-shrink-0">💡</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Memo */}
      {user && (
        <div className="animate-expand-child" style={{ animationDelay: `${childDelays[4]}s` }}>
          <div className={`rounded-xl p-5 border transition-all duration-500 ${memoSaved === step.id ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700' : 'bg-slate-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-slate-200 dark:bg-gray-600 rounded-lg flex items-center justify-center text-slate-600 dark:text-gray-300 flex-shrink-0">
                  <i className="ri-quill-pen-line text-sm"></i>
                </div>
                <h4 className="font-bold text-gray-900 dark:text-white text-sm">メモ・振り返り</h4>
              </div>
              {memoSaved === step.id && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                  <i className="ri-check-line"></i>保存しました
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">このステップで学んだこと、気づき、次に活かしたいことを自由に記録できます</p>
            {memoEditing === step.id ? (
              <div className="space-y-3">
                <textarea
                  value={memoTexts[step.id] ?? ''}
                  onChange={(e) => setMemoTexts((prev: any) => ({ ...prev, [step.id]: e.target.value }))}
                  placeholder="例：先行研究を読んで、〇〇という視点が抜けていたことに気づいた。次は△△の観点からも調べてみたい。"
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 resize-none leading-relaxed transition-shadow duration-300"
                />
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">{(memoTexts[step.id] ?? '').length} / 500</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setMemoEditing(null)}
                      className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={() => handleMemoSave(step.id)}
                      disabled={memoSaving === step.id}
                      className="px-4 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {memoSaving === step.id ? (
                        <span className="flex items-center gap-1">
                          <i className="ri-loader-4-line animate-spin text-xs"></i>保存中
                        </span>
                      ) : (
                        '保存する'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={() => {
                const p = getStepProgress(step.id);
                setMemoTexts((prev) => ({ ...prev, [step.id]: p?.memo || '' }));
                setMemoEditing(step.id);
              }} className="w-full text-left cursor-pointer group">
                {getStepProgress(step.id)?.memo ? (
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-100 dark:border-gray-600 group-hover:border-teal-200 dark:group-hover:border-teal-600 transition-all duration-300">
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{getStepProgress(step.id)?.memo}</p>
                    <p className="text-[11px] text-teal-600 dark:text-teal-400 mt-2 font-medium flex items-center gap-1">
                      <i className="ri-edit-line text-sm"></i>クリックして編集
                    </p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-dashed border-gray-200 dark:border-gray-600 group-hover:border-teal-300 dark:group-hover:border-teal-600 transition-all duration-300 flex items-center gap-2">
                    <i className="ri-add-line text-gray-400 dark:text-gray-500 group-hover:text-teal-500 transition-colors duration-300"></i>
                    <span className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors duration-300">メモを追加する...</span>
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Next Action */}
      {user && (
        <div className="animate-expand-child" style={{ animationDelay: `${childDelays[5]}s` }}>
          <div className="flex items-center gap-3 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-teal-200/60 dark:border-teal-700/40">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center text-white flex-shrink-0 shadow-sm">
              <i className="ri-arrow-right-circle-line text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-teal-700 dark:text-teal-400 mb-0.5">次にやること</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{getNextAction(step.id)}</p>
            </div>
            {step.portfolioLink && (
              <button
                onClick={() => scrollToSection(step.portfolioLink!.section)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors text-xs cursor-pointer whitespace-nowrap shadow-sm"
              >
                <i className="ri-file-text-line text-sm"></i>
                {step.portfolioLink.text}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResearchGuide({ user, scrollToSection }: ResearchGuideProps) {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const { updateStepStatus, getStepProgress, getCurrentStepIndex, getOverallProgress } = useStepProgress();
  const { toggleChecklistItem, getItemProgress, getStepCompletionRate } = useStepChecklist();

  const [updatingStep, setUpdatingStep] = useState<number | null>(null);
  const [togglingItem, setTogglingItem] = useState<string | null>(null);

  // Memo state
  const [memoTexts, setMemoTexts] = useState<Record<number, string>>({});
  const [memoEditing, setMemoEditing] = useState<number | null>(null);
  const [memoSaving, setMemoSaving] = useState<number | null>(null);
  const [memoSaved, setMemoSaved] = useState<number | null>(null);

  // Celebration state
  const [celebrationStep, setCelebrationStep] = useState<{ id: number; title: string } | null>(null);

  // Scroll auto-highlight
  const { setRef: setHighlightRef, highlightedStepId, triggerHighlight } = useScrollHighlight();

  const currentStepIndex = getCurrentStepIndex();

  const toggleStep = (stepId: number) => {
    const newId = expandedStep === stepId ? null : stepId;
    setExpandedStep(newId);
    if (newId !== null) {
      const progress = getStepProgress(newId);
      setMemoTexts((prev) => ({ ...prev, [newId]: progress?.memo || '' }));
    }
  };

  const handleStatusChange = async (stepId: number, newStatus: 'not_started' | 'in_progress' | 'completed') => {
    if (!user) return;
    try {
      setUpdatingStep(stepId);
      const currentProgress = getStepProgress(stepId);
      await updateStepStatus(stepId, newStatus, currentProgress?.memo || undefined);
    } catch {
      // silent
    } finally {
      setUpdatingStep(null);
    }
  };

  const handleChecklistToggle = async (stepId: number, itemId: number) => {
    if (!user) return;
    const key = `${stepId}-${itemId}`;
    try {
      setTogglingItem(key);
      await toggleChecklistItem(stepId, itemId);

      // Check if all items are now completed
      const checklist = stepChecklists[stepId] || [];
      const allCompleted = checklist.every((item) => {
        const progress = getItemProgress(stepId, item.id);
        return progress && progress.is_completed;
      });

      // Show celebration if all completed
      if (allCompleted && checklist.length > 0) {
        const step = researchSteps.find((s) => s.id === stepId);
        if (step) {
          setCelebrationStep({ id: step.id, title: step.title });
        }
      }
    } catch {
      // silent
    } finally {
      setTogglingItem(null);
    }
  };

  const handleMemoSave = useCallback(
    async (stepId: number) => {
      if (!user) return;
      const text = memoTexts[stepId] ?? '';
      try {
        setMemoSaving(stepId);
        const progress = getStepProgress(stepId);
        if (progress) {
          await supabase
            .from('research_step_progress')
            .update({ memo: text, updated_at: new Date().toISOString() })
            .eq('id', progress.id);
        } else {
          await supabase
            .from('research_step_progress')
            .insert({
              user_id: user.id,
              step_id: stepId,
              status: 'not_started',
              memo: text,
            });
        }
        setMemoSaved(stepId);
        setMemoEditing(null);
        setTimeout(() => setMemoSaved(null), 2000);
      } catch {
        // silent
      } finally {
        setMemoSaving(null);
      }
    },
    [user, memoTexts, getStepProgress],
  );

  const getNextAction = (stepId: number): string => {
    const checklist = stepChecklists[stepId] || [];
    const firstUncompleted = checklist.find((item) => {
      const progress = getItemProgress(stepId, item.id);
      return !progress || !progress.is_completed;
    });
    if (firstUncompleted) return firstUncompleted.text;
    return 'このステップのチェックリストを確認しましょう';
  };

  const getStatusBadge = (stepId: number) => {
    const progress = getStepProgress(stepId);
    if (!progress || progress.status === 'not_started') {
      return <span className="px-2.5 py-0.5 bg-slate-100 text-slate-500 text-[11px] font-semibold rounded-full whitespace-nowrap">未着手</span>;
    }
    if (progress.status === 'in_progress') {
      return <span className="px-2.5 py-0.5 bg-teal-100 text-teal-700 text-[11px] font-semibold rounded-full whitespace-nowrap">進行中</span>;
    }
    return (
      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[11px] font-semibold rounded-full flex items-center gap-0.5 whitespace-nowrap">
        <i className="ri-checkbox-circle-fill text-xs"></i>完了
      </span>
    );
  };

  return (
    <div>
      {/* Celebration Modal */}
      {celebrationStep && (
        <FullCompletionCelebration
          stepTitle={celebrationStep.title}
          stepId={celebrationStep.id}
          onClose={() => setCelebrationStep(null)}
          onNextStep={() => {
            const nextStep = researchSteps.find((s) => s.id === celebrationStep.id + 1);
            if (nextStep) {
              toggleStep(nextStep.id);
            }
          }}
        />
      )}

      {/* Progress Overview Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 mb-8 relative overflow-hidden shadow-sm">
        <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-teal-500 to-transparent rounded-bl-full opacity-80"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-emerald-500 to-transparent rounded-tr-full opacity-60"></div>
        <div className="relative">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-md shadow-teal-200/50">
                <i className="ri-route-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">全体の進捗</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  現在のステップ:{' '}
                  <span className="font-semibold text-teal-600 dark:text-teal-400">{researchSteps[currentStepIndex]?.title}</span>
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
                {getOverallProgress()}%
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {Math.round((getOverallProgress() / 100) * researchSteps.length)} / {researchSteps.length} ステップ完了
              </p>
            </div>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-400 rounded-full transition-all duration-700 ease-out relative"
              style={{ width: `${getOverallProgress()}%` }}
            >
              <div className="absolute inset-0 shimmer-line rounded-full"></div>
            </div>
          </div>
          {/* Mini step indicators */}
          <div className="flex justify-between mt-4">
            {researchSteps.map((step) => {
              const p = getStepProgress(step.id);
              const status = p?.status || 'not_started';
              return (
                <button
                  key={step.id}
                  onClick={() => toggleStep(step.id === expandedStep ? 0 : step.id)}
                  className="group flex flex-col items-center gap-1 cursor-pointer"
                  title={step.title}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 relative ${
                      status === 'completed'
                        ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200/50'
                        : status === 'in_progress'
                        ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 ring-2 ring-teal-400 animate-timeline-pulse'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 group-hover:bg-gray-200 dark:group-hover:bg-gray-600'
                    }`}
                  >
                    {status === 'completed' ? <i className="ri-check-line text-sm"></i> : step.id}
                  </div>
                  <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium hidden sm:block">{step.title.slice(0, 4)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Timeline Steps */}
      <div className="relative">
        <div className="absolute left-[23px] top-6 bottom-6 w-0.5 bg-gradient-to-b from-teal-300 via-emerald-200 to-teal-300 hidden sm:block"></div>

        <div className="space-y-4">
          {researchSteps.map((step, index) => {
            const progress = getStepProgress(step.id);
            const isUpdating = updatingStep === step.id;
            const status = progress?.status || 'not_started';
            const isExpanded = expandedStep === step.id;
            const completionRate = getStepCompletionRate(step.id, stepChecklists[step.id]?.length || 0);

            return (
              <StepCard
                key={step.id}
                step={step}
                index={index}
                progress={progress}
                isUpdating={isUpdating}
                status={status}
                isExpanded={isExpanded}
                completionRate={completionRate}
                user={user}
                toggleStep={toggleStep}
                handleStatusChange={handleStatusChange}
                getStatusBadge={getStatusBadge}
                getItemProgress={getItemProgress}
                togglingItem={togglingItem}
                handleChecklistToggle={handleChecklistToggle}
                getStepProgress={getStepProgress}
                memoTexts={memoTexts}
                setMemoTexts={setMemoTexts}
                memoEditing={memoEditing}
                setMemoEditing={setMemoEditing}
                memoSaving={memoSaving}
                memoSaved={memoSaved}
                handleMemoSave={handleMemoSave}
                getNextAction={getNextAction}
                scrollToSection={scrollToSection}
                setHighlightRef={setHighlightRef}
                isHighlighted={highlightedStepId === step.id}
                triggerHighlight={triggerHighlight}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StepCard({
  step,
  index,
  status,
  isUpdating,
  isExpanded,
  completionRate,
  user,
  toggleStep,
  handleStatusChange,
  getStatusBadge,
  getItemProgress,
  togglingItem,
  handleChecklistToggle,
  getStepProgress,
  memoTexts,
  setMemoTexts,
  memoEditing,
  setMemoEditing,
  memoSaving,
  memoSaved,
  handleMemoSave,
  getNextAction,
  scrollToSection,
  setHighlightRef,
  isHighlighted,
  triggerHighlight,
}: any) {
  const { ref, isVisible } = useInView(0.08);
  const highlightRef = useRef<HTMLDivElement>(null);

  // Auto-highlight in-progress steps when they scroll into view
  useEffect(() => {
    const node = highlightRef.current;
    if (!node || status !== 'in_progress') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          triggerHighlight(step.id);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5, rootMargin: '0px 0px -60px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [status, step.id, triggerHighlight]);

  // Register ref for parent tracking
  useEffect(() => {
    setHighlightRef(step.id, highlightRef.current);
    return () => setHighlightRef(step.id, null);
  }, [step.id, setHighlightRef]);

  return (
    <div ref={ref} className="relative">
      {/* Timeline Node */}
      <div className="absolute left-0 top-5 z-10 hidden sm:block">
        <div
          className={`w-[47px] h-[47px] rounded-full flex items-center justify-center border-[3px] transition-all duration-500 ${
            status === 'completed'
              ? 'bg-emerald-500 border-emerald-200 text-white shadow-lg shadow-emerald-200/50'
              : status === 'in_progress'
              ? 'bg-white dark:bg-gray-800 border-teal-400 text-teal-600 dark:text-teal-400 shadow-lg shadow-teal-100/50 animate-timeline-pulse'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500'
          }`}
        >
          {status === 'completed' ? (
            <i className="ri-check-line text-xl"></i>
          ) : (
            <span className="text-sm font-bold">{step.id}</span>
          )}
        </div>
      </div>

      {/* Step Card */}
      <div
        ref={highlightRef}
        className={`sm:ml-16 bg-white dark:bg-gray-800 rounded-2xl border overflow-hidden transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        } ${
          isHighlighted
            ? 'border-teal-400 shadow-2xl shadow-teal-100/60 ring-2 ring-teal-300/40 scroll-highlight-glow'
            : isExpanded
            ? 'border-teal-200 dark:border-teal-700 shadow-xl shadow-teal-50/50'
            : status === 'completed'
            ? 'border-emerald-100 dark:border-emerald-800/50 hover:border-emerald-200 dark:hover:border-emerald-700 hover:shadow-md'
            : status === 'in_progress'
            ? 'border-teal-100 dark:border-teal-800/50 hover:border-teal-200 dark:hover:border-teal-700 hover:shadow-md'
            : 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-md'
        }`}
        style={{ transitionDelay: isVisible ? `${index * 0.07}s` : '0s' }}
      >
        {/* Colored top accent */}
        <div className={`h-1 bg-gradient-to-r ${step.color} transition-all duration-500 ${isExpanded ? 'h-1.5' : isHighlighted ? 'h-1.5' : 'h-1'}`}></div>

        {/* Highlight indicator badge */}
        {isHighlighted && (
          <div className="px-5 pt-3 pb-0">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700 rounded-full animate-fade-in-up">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
              </span>
              <span className="text-[11px] font-semibold text-teal-700 dark:text-teal-400">現在取り組み中のステップ</span>
            </div>
          </div>
        )}

        {/* Card Header */}
        <button onClick={() => toggleStep(step.id)} className="w-full px-5 py-4 flex items-center gap-4 cursor-pointer text-left">
          <div className={`w-11 h-11 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center text-white text-lg flex-shrink-0 shadow-sm transition-transform duration-300 ${isExpanded ? 'scale-110' : ''}`}>
            <i className={step.icon}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wider">STEP {step.id}</span>
              {getStatusBadge(step.id)}
              {completionRate > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-12 bg-gray-100 dark:bg-gray-700 rounded-full h-1 overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${step.color} rounded-full transition-all duration-500`}
                      style={{ width: `${completionRate}%` }}
                    ></div>
                  </div>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">{completionRate}%</span>
                </div>
              )}
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white">{step.title}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.subtitle}</p>
          </div>
          <div className={`w-7 h-7 flex items-center justify-center rounded-full transition-all duration-400 ${isExpanded ? 'bg-teal-50 dark:bg-teal-900/40 rotate-180' : 'bg-gray-50 dark:bg-gray-700'}`}>
            <i className="ri-arrow-down-s-line text-lg text-gray-400 dark:text-gray-500"></i>
          </div>
        </button>

        {/* Status Control */}
        {user && (
          <div className="flex items-center gap-2 px-5 pb-3 -mt-1">
            <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-700 rounded-full p-1">
              {(['not_started', 'in_progress', 'completed'] as const).map((s) => {
                const isActive = status === s || (!getStepProgress(step.id) && s === 'not_started');
                const labels: Record<string, string> = { not_started: '未着手', in_progress: '進行中', completed: '完了' };
                const activeStyles: Record<string, string> = {
                  not_started: 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 shadow-sm',
                  in_progress: 'bg-teal-500 text-white shadow-sm',
                  completed: 'bg-emerald-500 text-white shadow-sm',
                };
                return (
                  <button
                    key={s}
                    onClick={() => handleStatusChange(step.id, s)}
                    disabled={isUpdating}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-300 whitespace-nowrap ${
                      isActive ? activeStyles[s] : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                    } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {s === 'completed' && <i className="ri-check-line mr-0.5"></i>}
                    {labels[s]}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Expanded Content */}
        {isExpanded && (
          <ExpandedContent
            step={step}
            user={user}
            completionRate={completionRate}
            stepChecklists={stepChecklists}
            getItemProgress={getItemProgress}
            togglingItem={togglingItem}
            handleChecklistToggle={handleChecklistToggle}
            getStepProgress={getStepProgress}
            memoTexts={memoTexts}
            setMemoTexts={setMemoTexts}
            memoEditing={memoEditing}
            setMemoEditing={setMemoEditing}
            memoSaving={memoSaving}
            memoSaved={memoSaved}
            handleMemoSave={handleMemoSave}
            getNextAction={getNextAction}
            scrollToSection={scrollToSection}
          />
        )}
      </div>
    </div>
  );
}
